import React, { useState, useEffect, useCallback, useMemo } from "react";
import Button from "../../components/Button/Button";
import { FaFileExcel, FaUserGraduate } from "react-icons/fa";
import AssessmentInfo from "./AssessmentInfo";
import "./AssessmentMarkingForm.css";
import Loader from "../../components/Loader/Loader";
import Modal from "../../components/Modal/Modal";
import * as XLSX from "xlsx";
import MarkingExcelBulkUpload from "./MarkingExcelBulkUpload";
import ValidationErrorsModal from "./ValidationErrorsModal";
import MarkingResultView from "./MarkingResultView";
import { useNavigate, useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  getAssessmentParticipants,
  createAssessmentMarking,
  updateAssessmentMarking,
} from "../../services/AssessmentMarkingService";
import {
  viewReportCard,
  downloadReportCard,
} from "../../services/ReportCardService";
import DataNotFound from "../../utils/DataNotFound";
import AssessmentMarkingForm from "./AssessmentMarkingForm";

// Whole number or decimal only: 9, 10, 9.5, 24.50, 0.5.
// Rejects "8 9", "9..8", "98   .8", "9.", ".5", "08", "-5", "24.50m".
const VALID_MARKS_REGEX = /^(0|[1-9]\d*)(\.\d+)?$/;
const INVALID_MARKS_MESSAGE =
  "Marks must be a whole number or decimal, e.g. 9, 9.5 or 24.50 (no spaces, letters, extra dots or leading zeros).";
const isValidMarks = (value) => VALID_MARKS_REGEX.test(String(value ?? ""));
// Same remark choices as the scholastic remark dropdown on the marking page
const SCHOLASTIC_REMARK_OPTIONS = ["AB", "ML", "LL", "L", "-"];
// Extra Excel dropdown choice that resets a remark picked by mistake
const REMARK_CLEAR = "CLEAR";
// Excel expression: true when the remark cell holds a real remark
const excelHasRemark = (cell) =>
  `AND(TRIM(${cell})<>"",UPPER(TRIM(${cell}))<>"${REMARK_CLEAR}")`;
const EXCEL_REJECTED_TITLE = "Excel not uploaded";
const EXCEL_REJECTED_MESSAGE =
  "No marks were saved. Fix these in the Excel and upload the file again.";
const NO_MARKING_MESSAGE =
  "No marking entered. Enter marks, select an option or add a remark for at least one student, then submit.";
const NO_TRAIT_MESSAGE =
  "No trait value selected. Select a value for at least one trait, then submit.";

const AssessmentParticipantsMarking = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [eventInfo, setEventInfo] = useState({});
  const [gradeSchemeId, setGradeSchemeId] = useState("");
  const [event_marking, setEventMarking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [is_traits, setIsTraits] = useState(0);
  const [gradeSchemeName, setGradeScheme] = useState("");
  const [traits, setTraits] = useState([]);
  const [trait_marking, setTraitMarking] = useState([]);
  const [eventParticipants, setEventParticipants] = useState([]);
  const [editableStudentId, setEditableStudentId] = useState(null);
  const [isBulkUpload, setBulkUpload] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [resultData, setResultData] = useState([{}]);
  const [pdfUrlData, setPdfUrlData] = useState(null);
  const [pdfLoader, setPdfLoader] = useState(false);
  const [scoreSchemeOptionsHashMap, setScoreSchemeOptionsHashMap] = useState(
    {},
  );
  const [excelErrors, setExcelErrors] = useState([]);
  // { title, message, errors } shown in ValidationErrorsModal, or null
  const [validationPopup, setValidationPopup] = useState(null);
  const { id } = useParams();
  const remarkMap = useMemo(
    () => ({
      "Student has scored above 50% in all subjects":
        "Congratulations! Passed and Promoted to Class _____",
      "Student has scored above 33% and below 50% in all subjects":
        "Passed and Promoted to Class _____",
      "Students promoted after giving grace marks": "Promoted to Class _____",
      "Student has scored above 25% and less than 33% in 2 subjects":
        "Promotion granted after clearing the Retest in _____ and _____",
      "Student has scored less than 25% in 2 or more than 2 subjects":
        "Promotion not granted",
      "Student has scored below 33%": "Promotion granted under NDP",
    }),
    [],
  );

  const fetchData = useCallback(async (flag) => {
    try {
      const response = await getAssessmentParticipants(params.id);
      if (response.status === 200) {
        const eventData = response.data.data;
        let gradeSchemename = eventData.grade_scheme.name.split("-");
        gradeSchemename = gradeSchemename[0] + " " + gradeSchemename[1];
        setGradeScheme(gradeSchemename);
        // Destructure eventData for better readability
        const {
          grade_scheme: gradeScheme,
          traits,
          event_participants,
        } = eventData;
        // Set state values
        setTraits(traits);
        setEventInfo(eventData);
        setEventParticipants(eventData.event_participants);
        setGradeSchemeId(gradeScheme.id);
        setIsTraits(eventData.is_traits);
        let hashMap = {};
        eventData.grade_scheme.ScoreOptions &&
          eventData.grade_scheme.ScoreOptions.forEach((option) => {
            hashMap[option.option_name.toUpperCase()] = option.id;
          });
        setScoreSchemeOptionsHashMap(hashMap);
        if (flag) {
          // Flatten traitMarking and event_marking arrays using flatMap
          const traitMarking = event_participants.flatMap((student) =>
            traits.map((traitMark) => {
              const marksData =
                student.marks_data &&
                student.marks_data.find(
                  (element) => element.trait_id === traitMark.subject_trait.id,
                );
              return {
                event_marking_id: marksData ? marksData.id : "",
                trait_id: traitMark.subject_trait
                  ? traitMark.subject_trait.id
                  : "",
                grade_scheme_id: traitMark.grade_scheme.id,
                student_id: student.id,
                option_id: marksData ? marksData.option_id : "",
                option_value: marksData ? marksData.option_value : "",
                remark: marksData ? marksData.remark : "",
              };
            }),
          );
          const eventMarking = event_participants.map((student) => ({
            student_id: student.id,
            event_marking_id: student.marks_data[0]
              ? student.marks_data[0].id
              : "",
            option_id: student.marks_data[0]
              ? student.marks_data[0].option_id
              : "",
            option_value: student.marks_data[0]
              ? student.marks_data[0].option_value
              : "",
            grade_scheme_id: student.marks_data[0]
              ? student.marks_data[0].grade_scheme_id
              : "",
            remark: student.marks_data[0] ? student.marks_data[0].remark : "",
          }));
          setTraitMarking(traitMarking);
          setEventMarking(eventMarking);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  const updatedfetchData = async (flag) => {
    try {
      const response = await getAssessmentParticipants(params.id);
      const eventData = response.data.data;

      let gradeSchemename = eventData.grade_scheme.name.split("-");
      gradeSchemename = gradeSchemename[0] + " " + gradeSchemename[1];
      setGradeScheme(gradeSchemename);
      // Destructure eventData for better readability
      const {
        grade_scheme: gradeScheme,
        traits,
        event_participants,
      } = eventData;

      // Set state values
      setTraits(traits);
      setEventInfo(eventData);
      setGradeSchemeId(gradeScheme.id);
      setIsTraits(eventData.is_traits);

      // Flatten traitMarking and event_marking arrays using flatMap
      const traitMarking = event_participants.flatMap((student) =>
        traits.map((traitMark, idx) => {
          const marksData =
            student.marks_data &&
            student.marks_data.find(
              (element) => element.trait_id === traitMark.subject_trait.id,
            );

          return {
            event_marking_id: marksData ? marksData.id : "",
            trait_id: traitMark.subject_trait ? traitMark.subject_trait.id : "",
            grade_scheme_id: traitMark.grade_scheme.id,
            student_id: student.id,
            option_id: marksData ? marksData.option_id : "",
            option_value: marksData ? marksData.option_value : "",
            remark: marksData ? marksData.remark : "",
          };
        }),
      );

      const eventMarking = event_participants.map((student) => ({
        student_id: student.id,
        event_marking_id: student.marks_data[0] ? student.marks_data[0].id : "",
        option_id: student.marks_data[0] ? student.marks_data[0].option_id : "",
        option_value: student.marks_data[0]
          ? student.marks_data[0].option_value
          : "",
        grade_scheme_id: student.marks_data[0]
          ? student.marks_data[0].grade_scheme_id
          : "",
        remark: student.marks_data[0] ? student.marks_data[0].remark : "",
      }));

      setTraitMarking(traitMarking);
      setEventMarking(eventMarking);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const tableContainer = document.querySelector(".table-container");
      const scrollLeft = tableContainer.scrollLeft;
      var scrollRight = scrollLeft;

      document
        .querySelectorAll(
          "th:first-child, td:first-child, th:nth-child(2), td:nth-child(2), th:nth-child(3), td:nth-child(3)",
        )
        .forEach((cell) => {
          cell.style.transform = `translateX(${scrollLeft}px)`;
        });

      document
        .querySelectorAll("th:last-child, td:last-child")
        .forEach((cell) => {
          // Assuming scrollRight is defined somewhere in your code or calculated
          cell.style.marginRight = `${scrollRight}px`;
        });
    };

    const tableContainer = document.querySelector(".table-container");
    // Ensure the table container is available
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
      // Clean up the event listener when the component is unmounted
      return () => {
        tableContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  useEffect(() => {
    fetchData(1);
  }, [params.id, fetchData]);

  const handleChange = useCallback(
    (event, student, index, schemeType) => {
      const { name, value } = event.target;
      const evaluationType = eventInfo.evaluation_type.name;
      if (
        evaluationType === "SCHOLASTIC ASSESSMENT" &&
        name === "option_value"
      ) {
        const isValid = /^[0-9]*\.?[0-9]*$/.test(value);
        if (!isValid) {
          enqueueSnackbar("Please enter valid marks", { variant: "error" });
          return;
        }
      }

      // if (name === "remark" && value.length > 250) return;

      const updatedEventMarking = event_marking.map((mark, idx) => {
        if (idx !== index) return mark;
        let updatedMark = {
          ...mark,
          student_id: student.id,
          grade_scheme_id: gradeSchemeId,
        };

        if (schemeType === "letter") {
          const selectedOption = eventInfo.grade_scheme.ScoreOptions.find(
            (option) => Number(option.id) === Number(value),
          );
          if (selectedOption) {
            updatedMark = {
              ...updatedMark,
              option_id: selectedOption.id,
              option_value: selectedOption.option_name,
              remark:
                evaluationType === "GENERAL REMARKS" ||
                gradeSchemeName === "SUBJECT REMARK"
                  ? selectedOption.option_name
                  : evaluationType === "REMARKS"
                    ? remarkMap[selectedOption.option_name]
                    : "",
            };
          }
        } else if (name === "option_value") {
          updatedMark = {
            ...updatedMark,
            option_id: "",
            option_value: value.replace(/[^0-9.]/g, ""),
          };
        } else {
          updatedMark = {
            ...updatedMark,
            [name]: value,
          };
        }
        return updatedMark;
      });
      setEventMarking(updatedEventMarking);
    },
    [event_marking, eventInfo, gradeSchemeId, gradeSchemeName, remarkMap],
  );

  const getTraitIndex = useCallback((studentID, traitID) => {
    let index = -1;
    trait_marking.forEach((mark, idx) => {
      if (
        Number(mark.student_id) === Number(studentID) &&
        Number(mark.trait_id) === Number(traitID)
      ) {
        index = idx;
      }
    });
    return index;
  }, [trait_marking]);

  const traitChangeHandler = useCallback(
    (event, studentID, traitID, studentIndex) => {
      const { value } = event.target;
      if (Number(value) === 0) return;
      const updatedMark = eventInfo.grade_scheme.ScoreOptions.find(
        (option) => Number(option.id) === Number(value),
      );
      const traitIndex = getTraitIndex(studentID, traitID);
      if (updatedMark === undefined) return;
      if (traitIndex !== -1) {
        const updatedTrait = {
          ...trait_marking[traitIndex],
          option_id: updatedMark.id !== undefined ? updatedMark.id : 0,
          option_value: updatedMark.option_name,
          remark: updatedMark.remark ? updatedMark.remark : "",
        };
        setTraitMarking((prevTraitMarking) => {
          const updatedTraitMarking = [...prevTraitMarking];
          updatedTraitMarking[traitIndex] = updatedTrait;
          return updatedTraitMarking;
        });

        let studentList = eventParticipants[studentIndex];
        const updatedTraitMarking = [...trait_marking];
        updatedTraitMarking[traitIndex] = updatedTrait;

        let markIndex = -1;
        studentList.marks_data.forEach((markData, idx) => {
          if (Number(markData.trait_id) === Number(traitID)) {
            markIndex = idx;
          }
        });

        let updatedMarksData = [...studentList.marks_data];
        updatedMarksData[markIndex] = {
          ...updatedMarksData[markIndex],
          option_id: updatedMark.id,
          option_value: updatedMark.option_name,
          remark: "",
        };

        setEventParticipants((prevEventParticipants) => {
          const updatedEventParticipants = [...prevEventParticipants];
          updatedEventParticipants[studentIndex] = {
            ...updatedEventParticipants[studentIndex],
            marks_data: updatedMarksData,
          };
          return updatedEventParticipants;
        });
      }
    },
    [eventInfo, trait_marking, eventParticipants, getTraitIndex],
  );

  const showValidationPopup = (title, message, errors = []) =>
    setValidationPopup({ title, message, errors });

  // Turns a failed save into modal rows. Server errors can be a string, an
  // array, or nested objects keyed like "event_marking.3.option_value"; every
  // message is listed, and indexed paths are matched back to the student in
  // the request that was sent.
  const getApiErrorRows = (error) => {
    const data = error?.response?.data;
    let payload = {};
    try {
      payload =
        typeof error?.config?.data === "string"
          ? JSON.parse(error.config.data)
          : error?.config?.data || {};
    } catch (e) {
      payload = {};
    }
    // Request body is { data: {...} } for marking saves
    const body = payload?.data ?? payload;

    const flat = [];
    const walk = (node, path) => {
      if (node === null || node === undefined || node === "") return;
      if (typeof node === "string" || typeof node === "number") {
        flat.push({ path, message: String(node) });
      } else if (Array.isArray(node)) {
        node.forEach((item, idx) =>
          walk(item, typeof item === "object" && item !== null ? `${path}.${idx}` : path),
        );
      } else if (typeof node === "object") {
        Object.entries(node).forEach(([key, value]) =>
          walk(value, path ? `${path}.${key}` : key),
        );
      }
    };
    const message = data?.message;
    walk(message?.errors, "");
    walk(message?.error, "");
    walk(data?.errors, "");
    walk(data?.error, "");
    if (typeof message === "string") walk(message, "");

    const seen = new Set();
    const rows = [];
    flat.forEach(({ path, message: text }) => {
      const key = `${path}|${text}`;
      if (seen.has(key)) return;
      seen.add(key);

      const match = /(event_marking|traits)\.(\d+)(?:\.(.+))?$/.exec(path);
      const entry = match ? body?.[match[1]]?.[Number(match[2])] : null;
      const student = entry
        ? eventParticipants.find((s) => Number(s.id) === Number(entry.student_id))
        : null;
      let field = match ? match[3] || "" : path;
      const value = entry && field ? entry[field] : "";
      if (field === "option_value") field = marksLabel;
      if (match?.[1] === "traits" && entry) {
        const trait = traits.find(
          (t) => Number(t.subject_trait.id) === Number(entry.trait_id),
        );
        if (trait) field = trait.subject_trait.name;
      }
      rows.push({
        admissionNo: student?.id_number ?? "",
        studentName: student ? `${student.first_name} ${student.last_name}` : "",
        column: field.replace(/_/g, " "),
        value: value ?? "",
        problem: text,
      });
    });
    return rows;
  };

  const showSaveFailed = (error) => {
    console.error("Error submitting data:", error);
    const rows = getApiErrorRows(error);
    const status = error?.response?.status;
    showValidationPopup(
      "Marks not saved",
      rows.length > 0
        ? "The server rejected the marking. Correct the problems below and submit again."
        : error?.response
          ? `The server could not save the marking (error ${status}). Please try again.`
          : "Could not reach the server. Check your connection and try again.",
      rows,
    );
  };

  // Returns false (and lists the students in the popup) if any entered
  // scholastic marks / attendance are not a plain whole number / decimal, or
  // are above the max score.
  const validateNumericMarks = (markings) => {
    if (!isNumericMarks) return true;
    const errors = [];
    markings.forEach((mark) => {
      if (
        !mark ||
        mark.option_value === "" ||
        mark.option_value === undefined ||
        mark.option_value === null
      )
        return;

      let problem = null;
      if (!isValidMarks(mark.option_value)) problem = INVALID_MARKS_MESSAGE;
      else if (isAboveMaxScore(mark.option_value))
        problem = `${marksLabel} cannot be more than the max score (${maxScore}).`;
      if (!problem) return;

      const student = eventParticipants.find(
        (s) => Number(s.id) === Number(mark.student_id),
      );
      errors.push({
        admissionNo: student?.id_number ?? "",
        studentName: student
          ? `${student.first_name} ${student.last_name}`
          : `Student ${mark.student_id}`,
        column: marksLabel,
        value: String(mark.option_value),
        problem,
      });
    });
    if (errors.length === 0) return true;

    showValidationPopup(
      `${marksLabel} not saved`,
      "Nothing was saved. Correct the values below and submit again.",
      errors,
    );
    return false;
  };

  const handleSubmit = async (event, student_id, index) => {
    event.preventDefault();
    if (!is_traits && !validateNumericMarks([event_marking[index]])) return;
    let final_traits = [];
    final_traits = trait_marking.filter(
      (marks) => Number(marks.student_id) === Number(student_id),
    );

    final_traits.forEach((mark) => {
      if (mark.event_marking_id === "") {
        delete mark["event_marking_id"];
      }
      if (mark.option_value === "") {
        mark.option_value = "options";
        mark.option_id = "";
      }
    });

    let Object = localStorage.getItem("user");
    let user = JSON.parse(Object);
    const user_id = user.id;
    const event_id = id;

    const data = {
      user_id,
      event_id,
      student_id: student_id,
      is_traits,
      traits: final_traits,
      event_marking: is_traits ? [] : [withRemarkOnlyZero(event_marking[index])],
    };

    try {
      if (editableStudentId) {
        const response = await updateAssessmentMarking({ data });
        if (response.status === 200) {
          enqueueSnackbar(response.data.message.success, {
            variant: "success",
          });
          if (eventInfo.evaluation_type.name !== "CO-SCHOLASTIC ASSESSMENT") {
            updatedfetchData(1);
          }
          fetchData(1);
        }
      } else {
        const response = await createAssessmentMarking({ data });
        if (response.status === 200) {
          enqueueSnackbar(response.data.message.success, {
            variant: "success",
          });
          fetchData(1);
        }
      }
      setEditableStudentId(null);
    } catch (error) {
      showSaveFailed(error);
    }
  };

  const editStudentMarksHandler = (student) => {
    setEditableStudentId(student.id);
    if (eventInfo.evaluation_type.name === "SCHOLASTIC ASSESSMENT")
      fetchData(1);
    else updatedfetchData();
  };

  // Co-scholastic (traits) and Letters/Text grade schemes are marked by
  // picking one of the grade scheme's score options, not by free text.
  const scoreOptions = useMemo(
    () => eventInfo.grade_scheme?.ScoreOptions || [],
    [eventInfo],
  );
  const isOptionMarking =
    Boolean(is_traits) ||
    eventInfo.grade_scheme?.type === "Letters" ||
    eventInfo.grade_scheme?.type === "Text";

  // option name (trimmed, upper-cased) → score option
  const scoreOptionLookup = useMemo(() => {
    const lookup = {};
    scoreOptions.forEach((option) => {
      lookup[String(option.option_name).trim().toUpperCase()] = option;
    });
    return lookup;
  }, [scoreOptions]);

  // Upper-cased, single-spaced so "Scholastic  assessment" still matches
  const normalizeName = (name) =>
    String(name ?? "").trim().replace(/\s+/g, " ").toUpperCase();
  const evaluationTypeName = normalizeName(eventInfo.evaluation_type?.name);
  const schemeName = normalizeName(gradeSchemeName);
  // Max score configured for this assessment; no limit if it isn't set
  const maxScore = Number(eventInfo.max_score);
  const hasMaxScore = Number.isFinite(maxScore) && maxScore > 0;
  const isAboveMaxScore = (value) => hasMaxScore && Number(value) > maxScore;
  // Marks are entered as numbers (not picked from a dropdown)
  const isScholasticMarks =
    !isOptionMarking &&
    (evaluationTypeName === "SCHOLASTIC ASSESSMENT" ||
      schemeName === "SCHOLASTIC ASSESSMENT") &&
    schemeName !== "SUBJECT REMARK";
  // Attendance is a number too (days present), limited by the max score
  const isAttendanceMarks =
    !isOptionMarking && evaluationTypeName.includes("ATTENDANCE");
  // option_value must be a plain number within 0..max score. Besides
  // Scholastic and Attendance, this covers any other typed-in (non-dropdown)
  // evaluation type that has a max score above 0.
  const isNumericMarks =
    !is_traits &&
    !isOptionMarking &&
    (isScholasticMarks || isAttendanceMarks || hasMaxScore);
  const marksLabel = isAttendanceMarks ? "Attendance" : "Marks";
  // Remark is picked from SCHOLASTIC_REMARK_OPTIONS (matches the marking page)
  const isScholasticRemark =
    !is_traits &&
    evaluationTypeName === "SCHOLASTIC ASSESSMENT" &&
    schemeName !== "SUBJECT REMARK";
  // Subject Remark / General Remarks: a remark alone (no option_value) is a
  // complete marking and is saved with option_value 0.
  const isRemarkEvent =
    !is_traits &&
    (evaluationTypeName === "GENERAL REMARKS" ||
      evaluationTypeName === "SUBJECT REMARKS" ||
      schemeName === "SUBJECT REMARK");
  const hasValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== "";
  const isRemarkOnly = (mark) =>
    isRemarkEvent && mark && !hasValue(mark.option_value) && hasValue(mark.remark);
  const withRemarkOnlyZero = (mark) =>
    isRemarkOnly(mark) ? { ...mark, option_value: "0", option_id: "" } : mark;
  // Scholastic, Subject Remark and General Remarks: when a remark is entered,
  // option_value is 0.
  const isRemarkZeroMarking =
    !isOptionMarking &&
    (evaluationTypeName === "SCHOLASTIC ASSESSMENT" ||
      evaluationTypeName === "GENERAL REMARKS" ||
      schemeName === "SUBJECT REMARK");

  const downloadExcel = async () => {
    const markingColumns = is_traits
      ? eventInfo.traits.map((trait) => trait.subject_trait.name)
      : ["option_value"];
    const columns = [
      "event_name",
      "student_name",
      "student_roll_no",
      "student_admission_no",
      "student_class",
      ...markingColumns,
      ...(is_traits ? [] : ["remark"]),
    ];

    const { default: ExcelJS } = await import("exceljs");
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Sheet1");
    ws.columns = columns.map((key) => ({
      header: key,
      key,
      width: Math.max(15, key.length + 4),
    }));
    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: "frozen", ySplit: 1 }];

    eventParticipants.forEach((student) => {
      ws.addRow({
        event_name: eventInfo.name.name,
        student_name: `${student.first_name} ${student.last_name}`,
        student_roll_no: student.roll_no,
        student_admission_no: student.id_number,
        student_class: eventInfo.program.name,
      });
    });

    if (isOptionMarking && scoreOptions.length > 0) {
      // Keep the option list on a hidden sheet: an inline list is capped at
      // 255 characters and breaks on option names containing commas.
      const optionsSheet = wb.addWorksheet("Options");
      scoreOptions.forEach((option, idx) => {
        optionsSheet.getCell(idx + 1, 1).value = String(option.option_name);
      });
      optionsSheet.state = "veryHidden";

      const validation = {
        type: "list",
        allowBlank: true,
        formulae: [`Options!$A$1:$A$${scoreOptions.length}`],
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: "Invalid value",
        error: "Please select a value from the dropdown list.",
        showInputMessage: true,
        promptTitle: "Select option",
        prompt: "Choose a value from the dropdown list.",
      };
      markingColumns.forEach((columnKey) => {
        const columnIndex = columns.indexOf(columnKey) + 1;
        for (let row = 2; row <= eventParticipants.length + 1; row++) {
          ws.getCell(row, columnIndex).dataValidation = validation;
        }
      });
    }

    if (isRemarkZeroMarking) {
      // option_value shows 0 as soon as a remark is typed; typing marks
      // over the cell replaces the formula. The upload applies the same rule.
      const remarkLetter = ws.getColumn("remark").letter;
      const optionValueColumn = ws.getColumn("option_value").number;
      for (let row = 2; row <= eventParticipants.length + 1; row++) {
        ws.getCell(row, optionValueColumn).value = {
          formula: `IF(${excelHasRemark(`${remarkLetter}${row}`)},0,"")`,
          result: "",
        };
      }
      wb.calcProperties.fullCalcOnLoad = true;
    }

    if (isNumericMarks) {
      // Excel refuses typed text like "8 9" or "9..8" and values above the
      // max score; pasted values are caught by the upload check.
      const optionValueColumn = ws.getColumn("option_value").number;
      const optionValueLetter = ws.getColumn("option_value").letter;
      const remarkLetter = isRemarkZeroMarking
        ? ws.getColumn("remark").letter
        : null;
      const lastRow = eventParticipants.length + 1;
      for (let row = 2; row <= lastRow; row++) {
        const cellRef = `${optionValueLetter}${row}`;
        // With a remark (AB, ML, ...) the only allowed value is 0
        const rangeRule = remarkLetter
          ? {
              type: "custom",
              formulae: [
                `AND(ISNUMBER(${cellRef}),${cellRef}>=0${
                  hasMaxScore ? `,${cellRef}<=${maxScore}` : ""
                },OR(NOT(${excelHasRemark(`${remarkLetter}${row}`)}),${cellRef}=0))`,
              ],
            }
          : {
              type: "decimal",
              ...(hasMaxScore
                ? { operator: "between", formulae: [0, maxScore] }
                : { operator: "greaterThanOrEqual", formulae: [0] }),
            };
        ws.getCell(row, optionValueColumn).dataValidation = {
          ...rangeRule,
          // Excel skips a custom rule entirely when "Ignore blank" is on and
          // a cell it refers to (the remark) is empty, so it must be off
          // here. Clearing a cell is still allowed (deleting is not validated).
          allowBlank: !remarkLetter,
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: `Invalid ${marksLabel.toLowerCase()}`,
          error:
            (hasMaxScore
              ? `Enter only a number from 0 to ${maxScore} (max score), e.g. 9, 10 or 9.5.`
              : "Enter only a whole number or decimal, e.g. 9, 10, 9.5 or 24.50.") +
            (remarkLetter
              ? " When a remark (AB, ML, ...) is selected, marks must be 0."
              : ""),
          showInputMessage: true,
          promptTitle: hasMaxScore ? `${marksLabel} (max ${maxScore})` : marksLabel,
          prompt:
            `Enter a number like 9, 10 or 9.5${
              hasMaxScore ? ` (0 to ${maxScore} only)` : ""
            }.` + (remarkLetter ? " If a remark is selected, marks are 0." : ""),
        };
      }

      if (lastRow >= 2) {
        // Pasting skips data validation, so any value that is not a number
        // or is outside 0..max score is also flagged red.
        const ref = `$${optionValueLetter}2`;
        ws.addConditionalFormatting({
          ref: `${optionValueLetter}2:${optionValueLetter}${lastRow}`,
          rules: [
            {
              type: "expression",
              formulae: [
                `AND(${ref}<>"",OR(NOT(ISNUMBER(${ref})),${ref}<0${
                  hasMaxScore ? `,${ref}>${maxScore}` : ""
                }))`,
              ],
              style: {
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  bgColor: { argb: "FFFFC7CE" },
                },
                font: { color: { argb: "FF9C0006" } },
              },
            },
          ],
        });
      }

      if (remarkLetter && lastRow >= 2) {
        // Validation only runs on the cell being edited, so a remark picked
        // after marks were typed is flagged red on both cells instead.
        ws.addConditionalFormatting({
          ref: `${optionValueLetter}2:${optionValueLetter}${lastRow} ${remarkLetter}2:${remarkLetter}${lastRow}`,
          rules: [
            {
              type: "expression",
              formulae: [
                `AND(${excelHasRemark(`$${remarkLetter}2`)},$${optionValueLetter}2<>"",$${optionValueLetter}2<>0)`,
              ],
              style: {
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  bgColor: { argb: "FFFFC7CE" },
                },
                font: { color: { argb: "FF9C0006" } },
              },
            },
          ],
        });
      }
    }

    if (isScholasticRemark) {
      const remarkColumn = ws.getColumn("remark").number;
      // When marks drive the remark rule, the remark list is only offered
      // while that row's marks are empty or 0; otherwise the list points at
      // an empty cell, so Excel refuses any remark in that row.
      let lockedRemarkSheet = null;
      if (isRemarkZeroMarking) {
        lockedRemarkSheet = wb.addWorksheet("RemarkOptions");
        SCHOLASTIC_REMARK_OPTIONS.forEach((option, idx) => {
          lockedRemarkSheet.getCell(idx + 1, 1).value = option;
        });
        lockedRemarkSheet.getCell(SCHOLASTIC_REMARK_OPTIONS.length + 1, 1).value =
          REMARK_CLEAR;
        lockedRemarkSheet.state = "veryHidden";
      }
      const optionValueLetter = ws.getColumn("option_value").letter;
      // All remarks + CLEAR; when the row has marks, only CLEAR is offered
      const clearRow = SCHOLASTIC_REMARK_OPTIONS.length + 1;
      const remarkRange = `RemarkOptions!$A$1:$A$${clearRow}`;
      const clearOnlyRange = `RemarkOptions!$A$${clearRow}`;
      for (let row = 2; row <= eventParticipants.length + 1; row++) {
        const marksRef = `$${optionValueLetter}${row}`;
        ws.getCell(row, remarkColumn).dataValidation = {
          type: "list",
          // Same Excel quirk: with "Ignore blank" on, an empty marks cell
          // would switch this rule off and let any remark through.
          allowBlank: !lockedRemarkSheet,
          formulae: [
            lockedRemarkSheet
              ? `IF(OR(TRIM(${marksRef})="",${marksRef}=0),${remarkRange},${clearOnlyRange})`
              : `"${[...SCHOLASTIC_REMARK_OPTIONS, REMARK_CLEAR].join(",")}"`,
          ],
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: "Invalid remark",
          error: lockedRemarkSheet
            ? `Select a remark from the dropdown (${SCHOLASTIC_REMARK_OPTIONS.join(", ")}). A remark can only be selected when marks in this row are empty or 0. Choose ${REMARK_CLEAR} to remove a remark.`
            : `Please select a remark from the dropdown: ${SCHOLASTIC_REMARK_OPTIONS.join(", ")}, or ${REMARK_CLEAR} to remove it.`,
          showInputMessage: true,
          promptTitle: "Remark",
          prompt: lockedRemarkSheet
            ? `Select a remark (marks become 0). Selected by mistake? Choose ${REMARK_CLEAR} (or press Delete) to reset it.`
            : `Select a remark. Choose ${REMARK_CLEAR} (or press Delete) to reset it.`,
        };
      }

      // A cell reset with CLEAR shows as empty. Excel cannot change the value
      // without a macro, so the text is hidden (";;;" number format, white
      // font as a fallback); it is treated as "no remark" everywhere.
      const remarkLetter = ws.getColumn("remark").letter;
      const lastRow = eventParticipants.length + 1;
      if (lastRow >= 2) {
        ws.addConditionalFormatting({
          ref: `${remarkLetter}2:${remarkLetter}${lastRow}`,
          rules: [
            {
              type: "expression",
              formulae: [`UPPER(TRIM($${remarkLetter}2))="${REMARK_CLEAR}"`],
              style: {
                numFmt: ";;;",
                font: { color: { argb: "FFFFFFFF" } },
              },
            },
          ],
        });
      }
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${eventInfo.program.name}-${eventInfo.subject.name}-${eventInfo.name.name}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const allSubmitHandler = async (event) => {
    event.preventDefault();

    // Clone once (avoid mutation)
    const traitsClone = structuredClone(trait_marking);

    // New event markings only
    const marking = event_marking
      .filter(
        (mark) =>
          mark.event_marking_id === "" &&
          (mark.option_value !== "" || isRemarkOnly(mark)),
      )
      .map(withRemarkOnlyZero);

    // New traits only
    const newTraits = traitsClone.filter(
      ({ option_value, event_marking_id }) =>
        option_value !== "" && event_marking_id === "",
    );

    // Collect unique student IDs
    const studentIds = new Set(newTraits.map((t) => t.student_id));

    // Prepare final trait payload
    const finalTraits = traitsClone
      .filter((t) => studentIds.has(t.student_id))
      .map((t) => ({
        ...t,
        option_value: t.option_value || "options",
      }));

    // User & event info
    const { id: user_id } = JSON.parse(localStorage.getItem("user") || "{}");
    const event_id = id;

    // Validation
    if (is_traits && finalTraits.length === 0) {
      showValidationPopup("Nothing to save", NO_TRAIT_MESSAGE);
      return;
    }

    if (!is_traits && marking.length === 0) {
      showValidationPopup("Nothing to save", NO_MARKING_MESSAGE);
      return;
    }
    if (!is_traits && !validateNumericMarks(marking)) return;

    // Payload
    const data = {
      user_id,
      event_id,
      is_traits,
      traits: finalTraits,
      event_marking: is_traits ? [] : marking,
    };
    try {
      const response = await createAssessmentMarking({ data });

      if (response.status === 200) {
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        setTraitMarking([]);
        setEventMarking([]);
        fetchData(1);
      }
    } catch (error) {
      showSaveFailed(error);
    }
  };

  const rejectExcel = (input, errors) => {
    setExcelErrors(errors);
    showValidationPopup(EXCEL_REJECTED_TITLE, EXCEL_REJECTED_MESSAGE, errors);
    setEventMarking([]);
    setTraitMarking([]);
    // Allow re-selecting the same file after it has been fixed
    input.value = "";
  };

  // Validates every row of the uploaded Excel. If any row has a problem the
  // whole file is rejected and all problems are listed in a modal.
  const excelDataReader = (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    setEventMarking([]);
    setTraitMarking([]);
    setExcelErrors([]);

    const fileError = (problem) => ({
      row: "-",
      admissionNo: "",
      studentName: "",
      column: "",
      value: "",
      problem,
    });

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      let rows;
      let headers;
      try {
        const wb = XLSX.read(target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        headers = (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || []).map(
          (header) => String(header).trim(),
        );
      } catch (error) {
        rejectExcel(input, [fileError("The file could not be read as Excel.")]);
        return;
      }

      const markingColumns = is_traits
        ? traits.map((trait) => trait.subject_trait.name)
        : ["option_value"];
      const requiredColumns = [
        "student_admission_no",
        ...markingColumns,
        ...(isRemarkZeroMarking ? ["remark"] : []),
      ];
      const missingColumns = requiredColumns.filter(
        (column) => !headers.includes(column),
      );
      if (missingColumns.length > 0) {
        rejectExcel(
          input,
          missingColumns.map((column) => ({
            ...fileError(
              "Column is missing or renamed. Please use the downloaded Excel.",
            ),
            column,
          })),
        );
        return;
      }
      if (rows.length === 0) {
        rejectExcel(input, [fileError("The Excel has no student rows.")]);
        return;
      }

      const studentMap = eventParticipants.reduce((acc, student) => {
        acc[String(student.id_number).trim()] = student;
        return acc;
      }, {});
      const traitIdMap = traits.reduce((acc, trait) => {
        acc[trait.subject_trait.name] = trait.subject_trait.id;
        return acc;
      }, {});

      const errors = [];
      const seenStudents = new Set();
      const finalData = [];
      const traitData = [];

      rows.forEach((row, idx) => {
        const rowNo = idx + 2; // row 1 is the header
        const admissionNo = String(row.student_admission_no).trim();
        const student = studentMap[admissionNo];
        const rowError = (column, value, problem) =>
          errors.push({
            row: rowNo,
            admissionNo,
            studentName: student
              ? `${student.first_name} ${student.last_name}`
              : String(row.student_name ?? ""),
            column,
            value: String(value ?? ""),
            problem,
          });

        if (!student) {
          rowError(
            "student_admission_no",
            admissionNo,
            admissionNo === ""
              ? "Admission no is empty."
              : "No student with this admission no in this assessment.",
          );
          return;
        }
        if (seenStudents.has(student.id)) {
          rowError(
            "student_admission_no",
            admissionNo,
            "Student appears more than once in the Excel.",
          );
          return;
        }
        seenStudents.add(student.id);

        let remark = String(row.remark ?? "").trim();
        // CLEAR in the Excel dropdown means "no remark"
        if (isScholasticRemark && remark.toUpperCase() === REMARK_CLEAR) {
          remark = "";
        }
        if (isScholasticRemark && remark !== "") {
          const matched = SCHOLASTIC_REMARK_OPTIONS.find(
            (option) => option === remark.toUpperCase(),
          );
          if (!matched) {
            rowError(
              "remark",
              remark,
              `Not a valid remark. Select from the dropdown: ${SCHOLASTIC_REMARK_OPTIONS.join(", ")}.`,
            );
            return;
          }
          remark = matched;
        }

        if (isOptionMarking) {
          markingColumns.forEach((column) => {
            const rawValue = String(row[column]).trim();
            if (rawValue === "") {
              if (!is_traits && isRemarkEvent && remark !== "") {
                finalData.push({
                  student_id: student.id,
                  remark,
                  grade_scheme_id: gradeSchemeId,
                  option_value: "0",
                  option_id: "",
                });
              }
              return;
            }

            const option = scoreOptionLookup[rawValue.toUpperCase()];
            if (!option) {
              rowError(
                column,
                rawValue,
                "Not a valid option. Please select a value from the dropdown.",
              );
              return;
            }

            if (is_traits) {
              traitData.push({
                student_id: student.id,
                remark: "",
                grade_scheme_id: gradeSchemeId,
                trait_id: traitIdMap[column],
                option_value: String(option.option_name).toUpperCase(),
                option_id: option.id,
              });
            } else {
              finalData.push({
                student_id: student.id,
                remark,
                grade_scheme_id: gradeSchemeId,
                option_value: option.option_name,
                option_id: option.id,
              });
            }
          });
          return;
        }

        // A number cell comes back as a JS number (9.50 → 9.5); a text cell
        // comes back exactly as typed and is checked strictly below.
        let optionValue =
          typeof row.option_value === "number"
            ? String(row.option_value)
            : String(row.option_value ?? "").trim();
        if (isRemarkZeroMarking && remark !== "") {
          // Marks must be empty or 0 next to a remark (the Excel enforces it;
          // a pasted conflict is rejected rather than silently changed).
          if (optionValue !== "" && Number(optionValue) !== 0) {
            rowError(
              "option_value",
              row.option_value,
              `${marksLabel} must be empty or 0 when a remark ("${remark}") is selected. Remove the ${marksLabel.toLowerCase()} or the remark.`,
            );
            return;
          }
          optionValue = "0";
        }

        if (optionValue === "" && remark === "") {
          if (isScholasticMarks) {
            rowError("option_value", "", "Enter marks, or enter a remark.");
          }
          return;
        }

        if (isNumericMarks && !isValidMarks(optionValue)) {
          rowError("option_value", row.option_value, INVALID_MARKS_MESSAGE);
          return;
        }
        if (isNumericMarks && isAboveMaxScore(optionValue)) {
          rowError(
            "option_value",
            row.option_value,
            `${marksLabel} cannot be more than the max score (${maxScore}).`,
          );
          return;
        }

        finalData.push({
          student_id: student.id,
          remark,
          grade_scheme_id: gradeSchemeId,
          option_value: optionValue,
          option_id: scoreSchemeOptionsHashMap[optionValue],
        });
      });

      if (errors.length > 0) {
        rejectExcel(input, errors);
        return;
      }
      if (finalData.length === 0 && traitData.length === 0) {
        rejectExcel(input, [
          fileError("No marking found in the Excel. Please fill the marking and upload again."),
        ]);
        return;
      }

      setEventMarking(finalData);
      setTraitMarking(traitData);
      enqueueSnackbar(
        `Excel checked: ${rows.length} row(s) are valid. Click Submit Excel to save.`,
        { variant: "success" },
      );
    };
    reader.onerror = () =>
      rejectExcel(input, [fileError("The file could not be read.")]);
    reader.readAsBinaryString(file);
  };

  const excelDataSubmitHandler = async (event) => {
    event.preventDefault();
    if (excelErrors.length > 0) {
      showValidationPopup(EXCEL_REJECTED_TITLE, EXCEL_REJECTED_MESSAGE, excelErrors);
      return;
    }
    let traits_clone = JSON.parse(JSON.stringify(trait_marking));
    // Rows were validated in excelDataReader; re-check before saving
    const marking = event_marking;
    if (!is_traits && !validateNumericMarks(marking)) return;

    let create_trait = traits_clone.filter(
      (student) =>
        (student.event_marking_id === "" &&
          student &&
          student.option_value !== "") ||
        student.remark !== "",
    );
    let student_ids = [];

    create_trait.forEach((mark) => {
      if (!student_ids.includes(mark.student_id))
        student_ids.push(mark.student_id);
    });

    let traits_res = traits_clone.filter((student) =>
      student_ids.includes(student.student_id),
    );
    traits_res.map((student) => {
      if (student.option_value === "") {
        student.option_value = "options";
      }
      return student;
    });

    let Object = localStorage.getItem("user");
    let user = JSON.parse(Object);
    const user_id = user.id;
    const event_id = id;

    let trait_marking_data = trait_marking.filter(
      (student) =>
        student &&
        ((student.option_value !== "" && student.option_value !== undefined) ||
          student.option_value === "0"),
    );

    let traits_marking = trait_marking_data.map((student) => {
      student.option_value = student.option_value.toUpperCase();
      return student;
    });

    if (is_traits) {
      if (trait_marking_data.length === 0) {
        showValidationPopup("Nothing to save", NO_TRAIT_MESSAGE);
        return;
      }
    } else {
      if (marking.length === 0) {
        showValidationPopup("Nothing to save", NO_MARKING_MESSAGE);
        return;
      }
    }

    const data = {
      user_id,
      event_id,
      is_traits,
      traits: traits_marking,
      event_marking: is_traits ? [] : marking,
    };
    try {
      const response = await createAssessmentMarking({ data });
      if (response.status === 200) {
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        setTraitMarking([]);
        setEventMarking([]);
        fetchData(1);
      }
    } catch (error) {
      showSaveFailed(error);
      return error;
    }
  };

  const ResultViewHandler = async (student, index) => {
    const term = eventInfo.term;
    const student_id = student.id;
    const session_id = localStorage.getItem("session_id");
    setIsOpen(true);
    setPdfUrlData(null);

    if (eventInfo.evaluation_type.name === "REMARKS") {
      fetchStudentPdf(student_id, term);
      return;
    }

    try {
      setPdfLoader(true);
      const response = await viewReportCard(student_id, session_id, term);
      if (response.status === 200) {
        let resultData;
        if (response.data.data.term2) {
          resultData = response.data.data.term2;
        } else {
          resultData = response.data.data;
        }
        setResultData(resultData);
      }
    } catch (error) {
    } finally {
      setPdfLoader(false);
    }
  };

  const fetchStudentPdf = async (student_id, term) => {
    try {
      const session_id = localStorage.getItem("session_id");
      const response = await downloadReportCard(student_id, session_id, term, {
        responseType: "arraybuffer",
      });
      const data = response.data;
      let blob = new Blob([data], { type: "application/pdf" });
      let blobUrl = URL.createObjectURL(blob);
      setPdfUrlData(blobUrl);
    } catch (error) {
      enqueueSnackbar("Report Card not generated yet!", { variant: "error" });
      console.error("Error fetching PDF:", error);
    }
  };

  if (!loading) {
    const isEmpty = Object.keys(eventInfo).length === 0;
    if (isEmpty) return <DataNotFound message="No data found" />;
  }

  return (
    <React.Fragment>
      {loading ? (
        <Loader />
      ) : (
        <div className="">
          <AssessmentInfo
            eventInfo={eventInfo}
            back={() => navigate("/assessment/marking/list")}
          />
          {pdfLoader ? (
            <Loader />
          ) : (
            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} w="900px">
              <MarkingResultView
                resultData={resultData}
                remarkType={eventInfo && eventInfo.evaluation_type.name}
                pdfData={pdfUrlData}
              />
            </Modal>
          )}
          <div className="animated mt-2">
            <div className="card">
              <div className="card-header">
                <ValidationErrorsModal
                  isOpen={Boolean(validationPopup)}
                  onClose={() => setValidationPopup(null)}
                  title={validationPopup?.title}
                  message={validationPopup?.message}
                  errors={validationPopup?.errors}
                />
                {isBulkUpload ? (
                  <MarkingExcelBulkUpload
                    excelDataReader={excelDataReader}
                    excelDataSubmitHandler={excelDataSubmitHandler}
                    downloadExcel={downloadExcel}
                  />
                ) : (
                  <div className="event-participants-header">
                    <div className="event-participants-heading">
                      <div className="event-participants-icon">
                        <FaUserGraduate />
                      </div>
                      <div>
                        <div className="event-participants-eyebrow">Marking</div>
                        <div className="event-participants-title">Event Participants</div>
                      </div>
                    </div>
                    <Button
                      variant="success"
                      icon={<FaFileExcel />}
                      onClick={() => setBulkUpload(true)}
                    >
                      Bulk Upload
                    </Button>
                  </div>
                )}
              </div>
              <div className="card-body event-participants-body" style={{ overflow: "auto" }}>
                <AssessmentMarkingForm
                  eventInfo={eventInfo}
                  is_traits={is_traits}
                  event_marking={event_marking}
                  editableStudentId={editableStudentId}
                  gradeSchemeName={gradeSchemeName}
                  traits={traits}
                  trait_marking={trait_marking}
                  eventParticipants={eventParticipants}
                  traitChangeHandler={traitChangeHandler}
                  handleChange={handleChange}
                  allSubmitHandler={allSubmitHandler}
                  ResultViewHandler={ResultViewHandler}
                  handleSubmit={handleSubmit}
                  editStudentMarksHandler={editStudentMarksHandler}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default AssessmentParticipantsMarking;
